import { AxiosError } from 'axios';

import { metricsAxios } from 'api/apiBase/axios';

import type {
  SolMeteoraPairSettings,
  SolMeteoraPairSettingsApiError,
  SolMeteoraPairSettingsApiResult,
  SolMeteoraPairSettingsPutRequest,
  SolMeteoraPairSettingsValues,
} from './types';
import {
  SOL_METEORA_ENGINE_FLAGS,
  SOL_METEORA_INT_LIMITS,
  SOL_METEORA_OPTIONAL_ENGINE_FLAGS,
  SOL_METEORA_RAW_LIMITS,
  SOL_METEORA_TECHNICAL_FIELDS,
} from './types';

const settingsUrl = (pairId: number) => `/api/v1/sol-bot/pair/${pairId}/meteora-settings`;

/** Code of a `GET` body that does not match the contract. The panel then blocks Save. */
export const SOL_METEORA_SETTINGS_RESPONSE_INVALID = 'SETTINGS_RESPONSE_INVALID';

const DIGITS_PATTERN = /^\d+$/;

/** Keeps the text of the server (`error`) and the string code (`code`). */
export const toSolMeteoraPairSettingsApiError = (
  error: unknown,
): SolMeteoraPairSettingsApiError => {
  if (error instanceof AxiosError) {
    const body = error.response?.data as
      | { code?: unknown; error?: unknown; message?: unknown }
      | undefined;
    const code = typeof body?.code === 'string' && body.code ? body.code : null;
    const serverText =
      typeof body?.error === 'string' && body.error
        ? body.error
        : typeof body?.message === 'string' && body.message
          ? body.message
          : null;

    return {
      status: error.response?.status ?? null,
      code,
      message: serverText ?? error.message ?? 'Unknown error',
    };
  }

  return {
    status: null,
    code: null,
    message: error instanceof Error ? error.message : 'Unknown error',
  };
};

const call = async <T>(
  request: () => Promise<{ data: T }>,
): Promise<SolMeteoraPairSettingsApiResult<T>> => {
  try {
    const response = await request();

    return { ok: true, data: response.data };
  } catch (error) {
    return { ok: false, error: toSolMeteoraPairSettingsApiError(error) };
  }
};

type FieldRead<T> = { ok: true; value: T } | { ok: false };

/** A raw amount is a digit string. A safe integer number is also accepted. */
const readRawAmount = (value: unknown): FieldRead<string> => {
  if (typeof value === 'string' && DIGITS_PATTERN.test(value)) return { ok: true, value };
  if (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0) {
    return { ok: true, value: String(value) };
  }

  return { ok: false };
};

const readInteger = (value: unknown): FieldRead<number> => {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0) {
    return { ok: true, value };
  }
  if (typeof value === 'string' && DIGITS_PATTERN.test(value)) {
    const parsed = Number(value);

    if (Number.isSafeInteger(parsed)) return { ok: true, value: parsed };
  }

  return { ok: false };
};

export type SolMeteoraPairSettingsParseResult =
  | { ok: true; data: SolMeteoraPairSettings }
  | { ok: false; field: string };

/**
 * Parses the body of `GET`. The parser is strict.
 * A missing or malformed field fails the whole body, because a wrong default changes money limits:
 * a limit read as `null` shows "no limit", and the next Save removes the limit on the server.
 * The only exception is a switch of `SOL_METEORA_OPTIONAL_ENGINE_FLAGS`:
 * an older API does not return it, and the missing switch reads as `false`.
 */
export const parseSolMeteoraPairSettings = (
  data: unknown,
  pairId: number,
): SolMeteoraPairSettingsParseResult => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return { ok: false, field: 'body' };

  const body = data as Record<string, unknown>;
  const settings: Record<string, unknown> = {};

  for (const key of SOL_METEORA_ENGINE_FLAGS) {
    const value = body[key];

    if (value === undefined && SOL_METEORA_OPTIONAL_ENGINE_FLAGS.includes(key)) {
      settings[key] = false;
      continue;
    }

    if (typeof value !== 'boolean') return { ok: false, field: key };
    settings[key] = value;
  }

  for (const key of SOL_METEORA_RAW_LIMITS) {
    if (!(key in body)) return { ok: false, field: key };
    if (body[key] === null) {
      settings[key] = null;
      continue;
    }

    const amount = readRawAmount(body[key]);
    if (!amount.ok) return { ok: false, field: key };
    settings[key] = amount.value;
  }

  for (const key of SOL_METEORA_INT_LIMITS) {
    if (!(key in body)) return { ok: false, field: key };
    if (body[key] === null) {
      settings[key] = null;
      continue;
    }

    const limit = readInteger(body[key]);
    if (!limit.ok) return { ok: false, field: key };
    settings[key] = limit.value;
  }

  for (const key of SOL_METEORA_TECHNICAL_FIELDS) {
    const amount = readRawAmount(body[key]);
    if (!amount.ok) return { ok: false, field: key };
    settings[key] = amount.value;
  }

  const exists = body.exists;
  if (typeof exists !== 'boolean') return { ok: false, field: 'exists' };

  const version = readInteger(body.version);
  if (!version.ok) return { ok: false, field: 'version' };

  const bodyPairId = readInteger(body.pair_id);
  const updatedAt = body.updated_at;
  const updatedBy = readInteger(body.updated_by);

  return {
    ok: true,
    data: {
      ...(settings as unknown as SolMeteoraPairSettingsValues),
      pair_id: bodyPairId.ok ? bodyPairId.value : pairId,
      exists,
      version: version.value,
      updated_at: typeof updatedAt === 'string' && updatedAt ? updatedAt : null,
      updated_by: updatedBy.ok ? updatedBy.value : null,
    },
  };
};

/** Reads the settings. A pair without a row returns `exists=false` and `version=0`. */
export const getSolMeteoraPairSettings = async (
  pairId: number,
): Promise<SolMeteoraPairSettingsApiResult<SolMeteoraPairSettings>> => {
  const result = await call<unknown>(() => metricsAxios.get(settingsUrl(pairId)));

  if (!result.ok) return result;

  const parsed = parseSolMeteoraPairSettings(result.data, pairId);

  if (!parsed.ok) {
    return {
      ok: false,
      error: {
        status: 200,
        code: SOL_METEORA_SETTINGS_RESPONSE_INVALID,
        message: `The settings response has no valid field "${parsed.field}"`,
      },
    };
  }

  return { ok: true, data: parsed.data };
};

/**
 * Saves the settings. The server rejects a stale `expected_version` with 409.
 * `data` is `null` when the server returns no settings body. Read the settings again after a save.
 */
export const putSolMeteoraPairSettings = async (
  pairId: number,
  body: SolMeteoraPairSettingsPutRequest,
): Promise<SolMeteoraPairSettingsApiResult<SolMeteoraPairSettings | null>> => {
  const result = await call<unknown>(() => metricsAxios.put(settingsUrl(pairId), body));

  if (!result.ok) return result;

  const parsed = parseSolMeteoraPairSettings(result.data, pairId);

  return { ok: true, data: parsed.ok ? parsed.data : null };
};

/**
 * Body for the first settings row of a new pair (D1):
 * 7 engines on, the two Volume engines off, every limit `null`, `expected_version=0`.
 * The technical fields keep the values that `GET` returned (the defaults of the database).
 */
export const buildSolMeteoraPairSettingsCreateRequest = (
  current: SolMeteoraPairSettings,
): SolMeteoraPairSettingsPutRequest => ({
  swap_enabled: true,
  batch_enabled: true,
  buy_sell_enabled: true,
  counter_trade_enabled: true,
  balance_enabled: true,
  transfer_enabled: true,
  holders_enabled: true,
  volume_regular_enabled: true,
  volume_organic_enabled: true,
  max_buy_lamports: null,
  max_sell_base_raw: null,
  max_slippage_bps: null,
  max_wallets: null,
  ct_max_buy_lamports: null,
  ct_max_sell_base_raw: null,
  balance_max_refill_lamports: null,
  balance_max_base_input_raw: null,
  balance_max_threshold_margin_lamports: null,
  transfer_max_total_lamports: null,
  transfer_max_total_base_raw: null,
  transfer_max_destinations: null,
  max_fee_lamports: current.max_fee_lamports,
  max_rent_lamports: current.max_rent_lamports,
  min_sol_reserve_lamports: current.min_sol_reserve_lamports,
  balance_max_network_fee_lamports: current.balance_max_network_fee_lamports,
  expected_version: 0,
});

/**
 * Creates the settings row of a new Meteora pair.
 * The function reads the settings first. An existing row stays unchanged (`created=false`).
 */
export const createDefaultSolMeteoraPairSettings = async (
  pairId: number,
): Promise<SolMeteoraPairSettingsApiResult<{ created: boolean }>> => {
  const current = await getSolMeteoraPairSettings(pairId);

  if (!current.ok) return current;
  if (current.data.exists) return { ok: true, data: { created: false } };

  const saved = await putSolMeteoraPairSettings(
    pairId,
    buildSolMeteoraPairSettingsCreateRequest(current.data),
  );

  return saved.ok ? { ok: true, data: { created: true } } : saved;
};
